import { Flash } from '@/components/Flash';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Toaster } from '@/components/ui/sonner';
import type { User as UserType } from '@/types/user';
import { Link, usePage } from '@inertiajs/react';
import { Library, LogOut, MessageSquareMore, Settings, User, Workflow } from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
  flash: {
    notice?: string;
    error?: string;
  };
}

export default function ApplicationLayout({ children, flash }: LayoutProps) {
  const { props } = usePage();
  const currentUser = props.user as UserType;
  return (
    <div className="min-h-screen flex">
      <Flash {...flash} />

      {/* Only show sidebar when user is authenticated */}
      {currentUser && (
        <aside className="fixed left-0 top-0 h-screen w-16 border-r flex flex-col items-center py-4 bg-background dark:bg-zinc-800">
          <nav className="flex flex-col space-y-4">
            <Link
              href="/workflows"
              className="p-2 rounded-md hover:bg-muted dark:hover:bg-zinc-700 transition-colors"
              title="Workflows"
            >
              <Workflow className="h-5 w-5" />
            </Link>
            <Link
              href="/catalog/flow"
              className="p-2 rounded-md hover:bg-muted dark:hover:bg-zinc-700 transition-colors"
              title="Catalog"
            >
              <Library className="h-5 w-5" />
            </Link>
            <Link
              href="/chat"
              className="p-2 rounded-md hover:bg-muted dark:hover:bg-zinc-700 transition-colors"
              title="Chat UI"
            >
              <MessageSquareMore className="h-5 w-5" />
            </Link>
            <Link
              href="/dev"
              className="p-2 rounded-md hover:bg-muted dark:hover:bg-zinc-700 transition-colors"
              title="Dev Tools"
            >
              <Settings className="h-5 w-5" />
            </Link>
          </nav>

          {/* User info and actions pinned to bottom */}
          <div className="mt-auto mb-4 flex flex-col items-center space-y-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="w-12 h-12 p-0">
                  <User className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64" side="right">
                <div className="space-y-2">
                  <div className="text-sm">
                    <p className="font-medium">{currentUser.name}</p>
                    <p className="text-muted-foreground">{currentUser.email}</p>
                  </div>
                  <hr />
                  <Link href="/logout" method="delete" as="button" className="w-full">
                    <Button variant="outline" size="sm" className="w-full">
                      <LogOut className="h-4 w-4 mr-2" />
                      Logout
                    </Button>
                  </Link>
                </div>
              </PopoverContent>
            </Popover>
            <ThemeToggle />
          </div>
        </aside>
      )}

      <main className={`flex-1 ${currentUser ? 'ml-16' : ''}`}>{children}</main>
      <Toaster />
    </div>
  );
}
